import React from "react";
import { Link } from "react-router-dom";
import { 
  Shield, Award, Users, Package, Mail, Phone, MapPin, 
  Clock, Truck, CreditCard, HelpCircle, ChevronDown, ChevronUp,
  Star, Zap, Lock, Gift, RefreshCw, AlertCircle, Check
} from "lucide-react";

// ==================== ABOUT US PAGE ====================
export const AboutPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-black mb-2"><span className="brand-gradient-text">About BLK PACKS</span>™</h1>
        <p className="brand-gradient-text text-lg font-black mb-8 uppercase tracking-wider">Just hobby smoke, sealed heat, and big hit energy 💨</p>

        {/* Brand voice — verbatim from the founder. Don't water it down. */}
        <div className="card p-6 mb-6 border border-orange-500/30 bg-black/60">
          <h2 className="text-2xl font-black brand-gradient-text mb-4 flex items-center gap-2">
            <Award className="text-orange-400" /> Welcome to BLK PACKS
          </h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            Welcome to <strong className="text-white">BLK PACKS</strong> — where hobby culture, live breaks, pack drops, and next-gen AR experiences collide.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            We're not just another breaker platform. BLK PACKS was built for collectors chasing real hobby smoke — autos, grails, SSPs, monster pulls, sealed heat, and the adrenaline of the rip.
          </p>
          <p className="text-gray-300 mb-4 leading-relaxed">
            From live breaks and randomized pack drops to exclusive AR hobby moments, we're building a new lane for the trading card world. Every drop is designed to feel like an event. Every hit has motion behind it.
          </p>
        </div>

        {/* What we blend */}
        <div className="card p-6 mb-6 border border-orange-500/30 bg-black/60">
          <h2 className="text-2xl font-black brand-gradient-text mb-4 flex items-center gap-2">
            <Package className="text-orange-400" /> Our platform blends:
          </h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              "Live hobby breaks",
              "Limited-entry pack drops",
              "Chase-based experiences",
              "AR card & pack visuals",
              "Community-driven hobby energy",
              "Monster Meter AI card scanner",
            ].map((item) => (
              <li key={item} className="bg-black/50 border border-orange-500/20 rounded-lg px-4 py-3 text-white font-bold text-sm flex items-center gap-2">
                <span className="brand-gradient-text">●</span> {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Monster Meter AI feature highlight — added 2026-06-04 per user */}
        <div className="card p-6 mb-6 border border-orange-500/30 bg-black/60">
          <h2 className="text-2xl font-black brand-gradient-text mb-4 flex items-center gap-2">
            <Package className="text-orange-400" /> Monster Meter AI
          </h2>
          <p className="text-gray-300 mb-3 leading-relaxed">
            Snap the back of any sports trading card and our AI tells you exactly what you're holding —
            player, year, set, parallel, serial number, raw market range, PSA 10 potential. If it's a PSA
            slab, we pull the verified grade and full population report from the official PSA database in
            real time.
          </p>
          <p className="text-gray-400 leading-relaxed text-sm">
            Free to scan. No subscriptions. Built for collectors who want to know what they have before
            they list, trade, or grade.
          </p>
        </div>

        {/* The closer */}
        <div className="card p-6 mb-6 border border-orange-500/30 bg-black/60">
          <p className="text-gray-300 mb-4 leading-relaxed">
            Whether you're hunting rookie autos, ripping Bowman, chasing Pokémon heat, or stacking grails — <strong className="text-white">BLK PACKS is built for the collectors that live this hobby every day.</strong>
          </p>
          <div className="mt-6 text-center space-y-1">
            <p className="brand-gradient-text font-black text-xl uppercase tracking-widest">No fluff.</p>
            <p className="brand-gradient-text font-black text-xl uppercase tracking-widest">No fake hype.</p>
            <p className="brand-gradient-text font-black text-2xl uppercase tracking-widest">Just hobby smoke, sealed heat, and big hit energy. 💨</p>
          </div>
        </div>

        {/* Trust — EMERGE Ledger + Pack Points + Verifiable */}
        <div className="card p-6 border border-orange-500/30 bg-black/60">
          <h2 className="text-2xl font-black brand-gradient-text mb-4 flex items-center gap-2">
            <Shield className="text-orange-400" /> Backed by the EMERGE Ledger
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-black/60 border border-orange-500/30 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl brand-gradient-bg flex items-center justify-center shadow-[0_0_20px_-4px_rgba(249,115,22,0.7)]">
                <CreditCard className="text-black" size={22} strokeWidth={2.5} />
              </div>
              <h3 className="brand-gradient-text font-black uppercase tracking-wider text-sm mb-1">Pack Points</h3>
              <p className="text-gray-500 text-xs">1 PT = $1 USD. Top-up via Stripe + IAP.</p>
            </div>
            <div className="text-center p-4 bg-black/60 border border-orange-500/30 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl brand-gradient-bg flex items-center justify-center shadow-[0_0_20px_-4px_rgba(249,115,22,0.7)]">
                <Award className="text-black" size={22} strokeWidth={2.5} />
              </div>
              <h3 className="brand-gradient-text font-black uppercase tracking-wider text-sm mb-1">Public Proof</h3>
              <p className="text-gray-500 text-xs">Every pull & claim logged on-chain. Verify at /verify.</p>
            </div>
            <div className="text-center p-4 bg-black/60 border border-orange-500/30 rounded-lg">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl brand-gradient-bg flex items-center justify-center shadow-[0_0_20px_-4px_rgba(249,115,22,0.7)]">
                <Shield className="text-black" size={22} strokeWidth={2.5} />
              </div>
              <h3 className="brand-gradient-text font-black uppercase tracking-wider text-sm mb-1">Native iOS + Android</h3>
              <p className="text-gray-500 text-xs">App Store · Google Play · AR ready.</p>
            </div>
          </div>
        </div>

        {/* Collectibles disclaimer — added 2026-06 to clearly position
            BLK PACKS as a hobby/collectibles brand, NOT bullion or
            investment. Quick read here; full language lives in FAQ →
            "Collectibles & Materials" and Terms of Service §6. */}
        <div className="mt-8 p-4 rounded-xl bg-amber-500/5 border border-amber-500/30 text-left">
          <p className="text-amber-300 font-black text-xs uppercase tracking-widest mb-2">Important — Collectible Product</p>
          <p className="text-gray-400 text-xs leading-relaxed">
            BLK PACKS collectibles are <strong className="text-white">premium hobby collectibles — our Gold and Silver lines</strong> — sold as collectibles, not bullion, not investment-grade precious metal, not a security, and not a financial instrument. You're buying the physical card or note — and collecting the Moment it captures. Value comes from collectibility, limited runs, and ecosystem utility — not melt value. Buy what you love.{" "}
            <Link to="/faq#collectibles-materials" className="brand-gradient-text font-bold hover:brightness-110">Read the full disclosure →</Link>
          </p>
        </div>

        <div className="mt-6 text-center flex flex-wrap items-center justify-center gap-3">
          <Link to="/live" className="brand-gradient-btn px-6 py-3 rounded-xl font-black" data-testid="about-live-cta">Watch a Live Break</Link>
          <Link to="/drops" className="inline-block px-6 py-3 rounded-xl font-black bg-black/60 border border-orange-500/40 hover:border-orange-500/80 brand-gradient-text transition-colors">Browse Pack Drops</Link>
          <Link to="/faq" className="inline-block px-6 py-3 rounded-xl font-black bg-black/60 border border-orange-500/40 hover:border-orange-500/80 brand-gradient-text transition-colors">View FAQ</Link>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTACT PAGE ====================
export const ContactPage = () => {
  const [submitted, setSubmitted] = React.useState(false);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-2"><span className="brand-gradient-text">Contact</span> Us</h1>
        <p className="text-gray-400 mb-8">We're here to help with any questions about your orders or our products.</p>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div>
            <div className="card p-6 mb-6">
              <h2 className="text-xl font-bold text-white mb-4">Get In Touch</h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="text-orange-400 mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Email Support</p>
                    <a href="mailto:support@blkpacks.fun" className="text-orange-400 hover:underline">support@blkpacks.fun</a>
                    <p className="text-gray-500 text-sm mt-1">Response within 24-48 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Clock className="text-orange-400 mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Business Hours</p>
                    <p className="text-gray-400">Monday - Friday</p>
                    <p className="text-gray-400">9:00 AM - 6:00 PM CST</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin className="text-orange-400 mt-1" size={20} />
                  <div>
                    <p className="text-white font-medium">Mailing Address</p>
                    <p className="text-gray-400">BLK PACKS™</p>
                    <p className="text-gray-400">205 North Michigan Avenue, Suite 810</p>
                    <p className="text-gray-400">Chicago, IL 60601</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card p-6">
              <h2 className="text-xl font-bold text-white mb-4">Quick Links</h2>
              <ul className="space-y-2">
                <li><Link to="/live" className="text-orange-400 hover:underline" data-testid="contact-live-breaks-link">Live Breaks Schedule</Link></li>
                <li><Link to="/faq#live-breaks" className="text-orange-400 hover:underline">FAQ — Live Breaks</Link></li>
                <li><Link to="/faq" className="text-orange-400 hover:underline">Frequently Asked Questions</Link></li>
                <li><Link to="/shipping" className="text-orange-400 hover:underline">Shipping & Returns</Link></li>
                <li><Link to="/terms" className="text-orange-400 hover:underline">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-orange-400 hover:underline">Privacy Policy</Link></li>
              </ul>
            </div>

            {/* Live Breaks quick-answers — refunds / spot transfer / replay
                are the top 3 inbox topics, surface them right here so users
                don't have to file a ticket. */}
            <div className="card p-6 mt-6" data-testid="contact-live-breaks-faq">
              <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                <HelpCircle className="text-orange-400" size={20} />
                Live Breaks — Quick Answers
              </h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-white font-bold">Can I get a refund on a spot?</p>
                  <p className="text-gray-400">Pack Points are refunded ONLY if the host cancels the break. Once a spot is claimed and the webinar starts, all sales are final — your team's 4D AR experience filter has already minted to your locker.</p>
                </div>
                <div>
                  <p className="text-white font-bold">My card never showed up. What do I do?</p>
                  <p className="text-gray-400">Check your Locker first — pulls are minted within seconds. If it's still missing, email the order number + the break URL (the <code className="text-orange-300">/live/&hellip;</code> page).</p>
                </div>
                <div>
                  <p className="text-white font-bold">I missed the live webinar. Can I still watch it?</p>
                  <p className="text-gray-400">Yes — once the break ends the room flips to REPLAY mode. Open the same <Link to="/live" className="text-orange-400 hover:underline">/live</Link> link, scrub to your card's moment, and share the clip.</p>
                </div>
                <div>
                  <p className="text-white font-bold">Want to host your own break?</p>
                  <p className="text-gray-400">Send us your handle + the league/format you want to break. We'll set you up as a host and walk you through the dashboard.</p>
                </div>
              </div>
              <Link to="/faq#live-breaks" className="brand-gradient-text font-black text-xs uppercase tracking-wider mt-4 inline-block hover:brightness-110">
                Read the full Live Breaks FAQ →
              </Link>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="card p-6">
            <h2 className="text-xl font-bold text-white mb-4">Send Us a Message</h2>
            
            {submitted ? (
              <div className="bg-green-500/20 border border-green-500 rounded-xl p-6 text-center">
                <Check className="mx-auto text-green-500 mb-4" size={48} />
                <h3 className="text-white font-bold text-xl mb-2">Message Sent!</h3>
                <p className="text-gray-400">Thank you for contacting us. We'll respond within 24-48 business hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Your Name *</label>
                  <input type="text" className="input-field" required placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email Address *</label>
                  <input type="email" className="input-field" required placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Order Number (if applicable)</label>
                  <input type="text" className="input-field" placeholder="ORD-XXXXXX" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Subject *</label>
                  <select className="input-field" required data-testid="contact-subject-select">
                    <option value="">Select a topic</option>
                    <option value="live_break">Live Break Question</option>
                    <option value="live_break_refund">Live Break — Spot Refund</option>
                    <option value="order">Order Status</option>
                    <option value="shipping">Shipping Question</option>
                    <option value="product">Product Information</option>
                    <option value="ar">AR / 3D Experience</option>
                    <option value="pack_points">Pack Points / Wallet</option>
                    <option value="returns">Returns/Refunds</option>
                    <option value="account">Account Help</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Message *</label>
                  <textarea className="input-field min-h-[120px]" required placeholder="How can we help you?"></textarea>
                </div>
                <button type="submit" className="btn-primary w-full">Send Message</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== FAQ PAGE ====================
export const FAQPage = () => {
  const [openIndex, setOpenIndex] = React.useState(null);

  // Honour `#live-breaks` style hash deep-links from Footer / Contact —
  // scroll the matching <h2> into view and auto-open its first question.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) return;
    // tiny delay lets the page paint first
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, []);
  
  const faqs = [
    {
      category: "Live Breaks",
      anchor: "live-breaks",
      questions: [
        {
          q: "What is a Live Break?",
          a: "A Live Break is a scheduled card-rip event. You claim a spot for a specific team, the host opens a sealed box on camera, and every physical card pulled for your team gets shipped to your door — autos, hits, rookies, smoke chases. The moment you buy in, your spot also unlocks an exclusive 4D AR experience / team filter that lives in your locker so you've got something to flex on lock too. (The AR filter is the only thing that lands at purchase — the actual physical cards arrive after the live rip.)"
        },
        {
          q: "How do I buy a spot?",
          a: "Open /live, pick a break, scroll the Spots tab on the right, and tap any open team/slot. Spots are paid for in Pack Points ONLY (1 PT = $1 USD) — no Stripe at checkout, no IAP popup. If you're short on points, the app will offer to top up first."
        },
        {
          q: "What happens the moment I buy a spot?",
          a: "Three things, all instant: (1) your Pack Points balance is debited, (2) the 4D AR filter for your team mints to your locker immediately — you can rip the AR pack right away to see your team's AR experience come alive, (3) you're locked into the room for the live rip. After the host rips the sealed box, the physical cards pulled for your team are packed and shipped to your address."
        },
        {
          q: "When does the reveal happen?",
          a: "Each break has a scheduled time. When that time arrives (or when all spots sell out — whichever comes first), the reveal unlocks for everyone in the room simultaneously. Everyone watches together — same moment, same packs, same hits."
        },
        {
          q: "I missed the live break. Can I still watch it?",
          a: "Yes. Once a break ends, the room flips to REPLAY. The recording stays at /live/{break} with full controls — scrub anywhere, pause, jump straight to the moment your team's hits were pulled. Replays stay up indefinitely for everyone in the room."
        },
        {
          q: "What formats are there?",
          a: "Random (the randomizer assigns you a team/slot after purchase — cheap, fair), Pick Your Team (you buy a specific team, anything pulled from that team ships to you), Fixed Spots (generic numbered slots, e.g. #1–32 — good for player checklists), and Auction (live bidding on marquee teams)."
        },
        {
          q: "Can I get a refund on a spot?",
          a: "Pack Points are refunded ONLY if the host cancels the break before going live. Once a spot is claimed and the webinar starts, all sales are final — your team's 4D AR filter has already minted to your locker and the sealed box is queued for ripping."
        },
        {
          q: "How does shipping work for the physical pulls?",
          a: "After the rip wraps, admin sorts every team's cards and ships them individually — one tracking number per buyer, sent to the shipping address on your BLK PACKS account. You'll get a push + email + in-app notification the moment your parcel goes out, and the tracking number lands in your locker. When the parcel arrives, you can tap 'Confirm package received' on that notification so we know it landed safe. Shipping inside the US is included; international buyers currently can't claim physical Live Break spots."
        },
        {
          q: "Can I chat during the break?",
          a: "Yes. Every break room has native live chat at the bottom of the video — you can react with the host and trash-talk other collectors in real time. There's also a tap-to-react bar (🔥 ❤️ 💯 👏 😱 🎉) right above the chat input — tap-tap-tap to send streaming reactions up the screen, TikTok-style. Chat is open to anonymous viewers (read-only) and signed-in users (post + react). Reactions don't require sign-in. Chat persists for 50 messages after the break ends."
        },
        {
          q: "How is this different from a Twitch break?",
          a: "Three things: (1) your team's 4D AR filter auto-mints to your locker the second you buy in — no DMs, no waiting, (2) we ship the actual physical pulls straight to your door (one tracking number per buyer, handled by admin from inside the platform), and (3) every claim and reveal is logged on the public EMERGE Ledger so anyone can verify the break wasn't manipulated."
        },
        {
          q: "Want to host your own break?",
          a: "We're onboarding new breakers — email support@blkpacks.fun with your handle, the league(s) you break, and a sample of your work. Once approved you get the Host Dashboard, RTMP ingest, and a customisable schedule. White-label hosting is on the roadmap."
        }
      ]
    },
    {
      category: "What's New (Last 45 Days)",
      questions: [
        {
          q: "What is EMERGE?",
          a: "EMERGE is our new public authenticity ledger at /verify — a Solana Explorer–style page where anyone can verify a BLK PACKS collectible is real. Enter a Verification ID (format: COA-XX-YYYY-BLK000000), Serial #, or Card ID to see the card's full public record: rarity, serial, mint date, and collection. No login needed."
        },
        {
          q: "What are Verification IDs?",
          a: "Every collectible on the platform now carries a unique Verification ID like COA-ZK-2026-BLK000018. It's printed on the physical card (alongside the QR code) and also lives on the EMERGE ledger. It's the fastest way to prove a card is authentic without needing our app."
        },
        {
          q: "Can I verify a sealed pack before opening it?",
          a: "Yes. Scan the QR on a sealed pack and EMERGE will confirm the pack is genuine and unopened — without revealing the card inside (no spoilers). Once you rip the pack, the full COA becomes viewable."
        },
        {
          q: "Do I still need KYC to use BLK PACKS?",
          a: "No. KYC (identity verification) has been fully removed. You can buy, collect, trade, and claim rewards using just email signup. This boosts onboarding speed significantly."
        },
        {
          q: "What's 'Auto-Share Legendaries'?",
          a: "A new toggle in your Profile. When ON (default), pulling a Legendary rarity card shows you an instant 'Share on EMERGE' modal with a branded image for Instagram, TikTok, or X. Turn it off in Profile → Settings if you prefer privacy on big pulls."
        },
        {
          q: "Why is my AR card now smaller than before?",
          a: "We fixed a longstanding bug where iOS AR Quick Look placed 3D models at room-size (~1-2 meters). Cards now correctly appear at realistic trading-card size (~12 cm) when you launch AR. You can still pinch-to-zoom to make them bigger or smaller."
        },
        {
          q: "Why does my 3D model show a card image first while it loads?",
          a: "New loading UX — instead of a blank dark frame, you now see the 2D card art as a poster + a live 'Loading 3D · 42%' progress bar. Large 3D models take a few seconds on cellular; the poster keeps the view looking alive."
        },
        {
          q: "What is the 'PLAY-GROUND LEGENDS' section on the homepage?",
          a: "The new hero story section on the homepage. Every drop gives you a shot at all rarity tiers — Common, Rare, Epic, and Legendary. Pull your Kid, tap in, and watch him pull up in your world in AR."
        },
        {
          q: "Why was I logged out when my session ended?",
          a: "If you see a 'Session Ended' modal, it means your login token expired. Tap 'Log In Again' and you're back in seconds. We now handle expired sessions gracefully — no more blank buttons or dead links."
        }
      ]
    },
    {
      category: "Products & Purchases",
      questions: [
        {
          q: "What products does BLK PACKS™ sell?",
          a: "BLK PACKS™ sells premium collectible graded cards in four rarity tiers (Common, Rare, Epic, Legendary) paired with digital companion collectibles, AR experiences, and certificates of authenticity. Each purchase includes a physical collectible card shipped to you plus digital items accessible on our platform and native mobile app."
        },
        {
          q: "What are the purchase tiers?",
          a: "We offer fixed purchase tiers: $25, $50, $100, $250, $500, and up to $999. Each tier corresponds to specific collectible product packages. These are one-time retail purchases, not stored value or wallet deposits."
        },
        {
          q: "Are the collectibles authentic?",
          a: "Yes, all our collectible cards are professionally graded and authenticated. Each product includes a certificate of authenticity and can be verified through our platform using the serial number."
        },
        {
          q: "How do drops work?",
          a: "Drops are limited-time releases of collectible packs. You join a queue, and when it's your turn, you can purchase. Each pack contains a physical collectible card and digital collectible with varying rarities."
        }
      ]
    },
    {
      category: "Platform Points",
      questions: [
        {
          q: "What are platform participation points?",
          a: "Points are promotional engagement units received with your collectible purchases. They are NOT currency, have no cash value, and cannot be withdrawn, transferred, or redeemed for money. Points are used solely for on-platform activities like challenges and community events."
        },
        {
          q: "Can I withdraw or cash out points?",
          a: "No. Points have no monetary value and cannot be withdrawn, transferred, or converted to cash. They exist only for platform engagement purposes."
        },
        {
          q: "What are Pack Points?",
          a: "Pack Points are engagement units earned through purchases and platform participation. They are used ONLY to enter platform challenges—they have NO cash value and cannot be converted to money, withdrawn, or used to reduce purchase costs."
        },
        {
          q: "What do challenge winners receive?",
          a: "Challenge winners receive limited-edition physical collectible cards shipped to them, plus digital certificates of authenticity. All rewards are tangible or digital collectibles only—never cash or monetary prizes."
        }
      ]
    },
    {
      category: "Shipping & Delivery",
      questions: [
        {
          q: "Where do you ship?",
          a: "We ship to the United States only (all 50 states plus Puerto Rico). Shipping is a flat $7.50 USPS Priority Mail with signature required, and shipping is FREE on orders over $100. We do not currently ship internationally."
        },
        {
          q: "How long does shipping take?",
          a: "US orders typically arrive within 2-5 business days via USPS Priority Mail with signature required. International orders take 7-21 business days."
        },
        {
          q: "Is shipping insured?",
          a: "Yes, all shipments are insured and require signature upon delivery to protect your valuable collectibles."
        }
      ]
    },
    {
      category: "Returns & Refunds",
      questions: [
        {
          q: "What is your refund policy?",
          a: "Orders that have not shipped are eligible for cancellation and full refund. Shipped orders may be returned in accordance with our return policy. Refunds are issued to the original payment method. Digital items and points are non-refundable and will be deactivated upon qualifying refunds."
        },
        {
          q: "How do I request a refund?",
          a: "Contact support@blkpacks.fun with your order number and reason for the refund request. Our team will respond within 24-48 hours."
        },
        {
          q: "Are digital items refundable?",
          a: "No. Digital companion items and platform participation points have no monetary value and are non-refundable. If a physical product refund is approved, associated digital access is deactivated."
        }
      ]
    },
    {
      category: "Account & Security",
      questions: [
        {
          q: "How do I set up my profile?",
          a: "Go to your Profile page to complete your profile setup. Enable notifications to stay updated on new drops and exclusive offers."
        },
        {
          q: "Is my payment information secure?",
          a: "Yes. All payments are processed securely through Stripe and native in-app purchases (Apple App Store / Google Play). We never store your payment details on our servers."
        },
        {
          q: "How do I reset my password?",
          a: "Click 'Forgot Password' on the login page and enter your email. You'll receive a reset link within minutes."
        },
        {
          q: "How do I delete my account?",
          a: "Go to Profile > Settings > Delete Account. Account deletion is instant and permanent. All digital collectibles, Pack Points, and trade history will be permanently removed. This cannot be undone."
        }
      ]
    },
    {
      category: "AR & 3D Experiences",
      questions: [
        {
          q: "How does AR work on BLK PACKS™?",
          a: "Select AR-enabled cards have a 'Try AR' button. Tap it to load a 3D model. On iOS, this opens Apple AR Quick Look where you can place the 3D model on any surface (it'll appear at realistic trading-card size, ~12cm) and walk around it. On Android, Google Scene Viewer handles the AR. On the web, you see an interactive 3D preview with a live loading progress bar."
        },
        {
          q: "How do I scan a physical card for AR?",
          a: "AR-enabled physical cards have a QR code printed on them. Open your Locker, tap 'Scan Card', and point your camera at the QR code. This launches the AR experience for that specific card."
        },
        {
          q: "Does AR require an internet connection?",
          a: "Yes. The 3D models are loaded from BLK PACKS™ servers. You need an active internet connection to view AR content."
        },
        {
          q: "Does AR access my camera?",
          a: "Yes, temporarily. AR uses your camera to overlay 3D models onto your real environment. We do NOT record, store, or transmit any camera footage. Camera access stops the moment you close the AR view."
        }
      ]
    },
    {
      category: "Collectibles & Materials",
      anchor: "collectibles-materials",
      questions: [
        {
          q: "What is a BLK PACKS collectible?",
          a: "BLK PACKS is a collectible ecosystem that combines premium physical collectibles with digital experiences. Every release is designed to celebrate Moments That Matter through limited-edition collectibles, augmented reality (AR), exclusive digital content, and serial-numbered releases. These products are created for collectors and hobby enthusiasts — they are NOT bullion, NOT investment-grade precious metal products, and NOT a financial instrument."
        },
        {
          q: "What gives a BLK PACKS collectible its value?",
          a: "Value comes from the complete ecosystem: limited production runs, serial-numbered collectibles, exclusive artwork and designs, digital Moments That Matter, Augmented Reality (AR) experiences, community and platform utility, and collector demand and rarity. Like traditional trading cards and other collectibles, market value is determined by collectibility — not by the intrinsic value of the materials used."
        },
        {
          q: "Are BLK PACKS Gold and Silver collectibles bullion?",
          a: "No. BLK PACKS are premium hobby collectibles — sold, priced, and marketed as collectibles, not as bullion coins or bars, and not as investment-grade precious metal. Our materials, blend, construction, finishing, and manufacturing methods are proprietary. In other words, that's our Michael Jordan secret sauce — and we're keeping the recipe in the vault. 😉"
        },
        {
          q: "What are the Gold and Silver lines?",
          a: "They're our two product lines of premium hobby collectible notes and cards. You're buying the physical card or note — and what you're really collecting is the Moment it captures, just like the biggest trading card brands highlight their Moments. Value is driven by collectibility, rarity, serial numbers, and ecosystem utility."
        },
        {
          q: "Are these meant to be melted for metal value?",
          a: "No. A BLK PACKS collectible is worth more intact than destroyed — nobody melts a collectible note to get at the materials. Melting, refining, extracting, or reverse-engineering a BLK PACKS collectible is prohibited, voids its certificate of authenticity, all serial-number utility (locker, AR, Swap Spot, EMERGE ledger), and any buyback eligibility, and may constitute trade-secret misappropriation. We make BLK PACKS. We know how they're made. The secret sauce stays secret."
        },
        {
          q: "Is this an investment?",
          a: "No. BLK PACKS collectibles are not securities, not investments, and not a store of value. They are hobby-grade collectible products. Collector market value can fluctuate or go to zero, just like any other trading card or collectible. Buy what you love — don't buy what you can't afford to lose."
        }
      ]
    },
    {
      category: "Trading & Live Events",
      questions: [
        {
          q: "What is Tap Trade?",
          a: "Tap Trade is our instant card-swapping feature for live events. You select a card and get a 6-digit code. Share the code with another collector — they enter it, pick their card, and the trade completes instantly. Codes expire after 5 minutes."
        },
        {
          q: "What is Live Event Mode?",
          a: "Live Event Mode is a full-screen trading experience accessed from your Locker. It shows a large Tap Trade code, card selection grid, and a live leaderboard of top traders. It's designed for drops and collector meetups."
        },
        {
          q: "Can I trade with someone remotely?",
          a: "Yes. Use the 'Send Trade Request' option on any card in your Locker. The other collector has 24 hours to accept or reject. You can also use Event Trade for auto-matching at live events (15-minute window)."
        },
        {
          q: "Are trades reversible?",
          a: "No. Once a trade is completed, it is final and permanent. Both collectors' cards are swapped instantly. Make sure you want to trade before confirming."
        },
        {
          q: "What is the Trade Leaderboard?",
          a: "The Trade Leaderboard ranks collectors by total completed trades. It's visible in Live Event Mode and your Locker page. Top traders earn recognition in the community."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-2">Frequently Asked <span className="brand-gradient-text">Questions</span></h1>
        <p className="text-gray-400 mb-8">Find answers to common questions about BLK PACKS™ products and services.</p>
        
        {faqs.map((category, catIndex) => (
          <div key={catIndex} className="mb-8" id={category.anchor || undefined}>
            <h2 className="text-xl font-black brand-gradient-text mb-4 scroll-mt-24">{category.category}</h2>
            <div className="space-y-3">
              {category.questions.map((faq, faqIndex) => {
                const index = `${catIndex}-${faqIndex}`;
                const isOpen = openIndex === index;
                return (
                  <div key={index} className="card overflow-hidden">
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800/50 transition-colors"
                    >
                      <span className="text-white font-medium pr-4">{faq.q}</span>
                      {isOpen ? <ChevronUp className="text-orange-400 flex-shrink-0" /> : <ChevronDown className="text-gray-400 flex-shrink-0" />}
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-gray-400 border-t border-gray-800">
                        <p className="pt-4">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        <div className="card p-6 text-center">
          <HelpCircle className="mx-auto brand-gradient-text mb-4" size={48} />
          <h3 className="text-white font-bold text-xl mb-2">Still Have <span className="brand-gradient-text">Questions</span>?</h3>
          <p className="text-gray-400 mb-4">Our support team is here to help.</p>
          <Link to="/contact" className="btn-primary inline-block">Contact Support</Link>
        </div>
      </div>
    </div>
  );
};

// ==================== SHIPPING & RETURNS PAGE ====================
export const ShippingPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-2"><span className="brand-gradient-text">Shipping</span> & Returns</h1>
        <p className="text-gray-400 mb-8">Everything you need to know about receiving and returning your orders.</p>
        
        {/* Shipping Information */}
        <div className="card p-6 mb-6">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <Truck className="text-orange-400" /> Shipping Information
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="bg-orange-500/5 border border-orange-500/30 p-4 rounded-lg">
              <h3 className="brand-gradient-text font-black mb-2">United States</h3>
              <p className="text-3xl font-bold text-white mb-2">$15.00</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• All 50 states + Puerto Rico</li>
                <li>• USPS Priority Mail</li>
                <li>• 2-5 Business Days</li>
                <li>• Signature Required</li>
                <li>• Full Insurance Included</li>
              </ul>
            </div>
            <div className="bg-orange-500/5 border border-orange-500/30 p-4 rounded-lg">
              <h3 className="brand-gradient-text font-black mb-2">International</h3>
              <p className="text-3xl font-bold text-white mb-2">$45.00</p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• Worldwide (including Canada)</li>
                <li>• USPS International</li>
                <li>• 7-21 Business Days</li>
                <li>• Signature Required</li>
                <li>• DDP (All Duties Paid)</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-black/60 border border-white/10 p-4 rounded-lg">
            <h3 className="text-white font-bold mb-2">Shipping From</h3>
            <p className="text-gray-400">BLK PACKS™ • 205 North Michigan Avenue, Suite 810 • Chicago, IL 60601</p>
          </div>
        </div>
        
        {/* Returns & Refunds */}
        <div className="card p-6 mb-6">
          <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
            <RefreshCw className="text-orange-400" /> <span className="brand-gradient-text">Returns</span> & <span className="brand-gradient-text">Refunds</span>
          </h2>
          
          <div className="space-y-4">
            <div className="bg-orange-500/5 border border-orange-500/30 p-4 rounded-lg">
              <h3 className="brand-gradient-text font-black mb-2">Eligible for Refund</h3>
              <ul className="text-gray-400 space-y-1">
                <li>• Orders not yet shipped - Full refund</li>
                <li>• Damaged items - Full replacement or refund</li>
                <li>• Incorrect items received - Full replacement or refund</li>
                <li>• Returns within 14 days of delivery (unopened items)</li>
              </ul>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
              <h3 className="text-red-400 font-bold mb-2">Not Eligible for Refund</h3>
              <ul className="text-gray-400 space-y-1">
                <li>• Opened or used products</li>
                <li>• Digital companion items (no cash value)</li>
                <li>• Platform participation points (no cash value)</li>
                <li>• Market value fluctuations</li>
                <li>• Change of mind after opening</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <p className="brand-gradient-text font-black mb-2">Important Note</p>
            <p className="text-gray-400 text-sm">
              Refunds are processed to the original payment method only. We do NOT issue cash alternatives, 
              store credits with monetary value, or transferable balances. If a refund is approved, any 
              associated digital items and participation points will be deactivated.
            </p>
          </div>
        </div>
        
        {/* How to Return */}
        <div className="card p-6">
          <h2 className="text-2xl font-bold text-white mb-4">How to <span className="brand-gradient-text">Request a Return</span></h2>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="brand-gradient-bg text-black w-6 h-6 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              <div>
                <p className="text-white font-medium">Contact Support</p>
                <p className="text-gray-400 text-sm">Email <span className="brand-gradient-text font-semibold">support@blkpacks.fun</span> with your order number and reason for return.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="brand-gradient-bg text-black w-6 h-6 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <div>
                <p className="text-white font-medium">Receive Authorization</p>
                <p className="text-gray-400 text-sm">If approved, you'll receive a Return Authorization Number (RMA) and instructions.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="brand-gradient-bg text-black w-6 h-6 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              <div>
                <p className="text-white font-medium">Ship the Item</p>
                <p className="text-gray-400 text-sm">Send the item back in original condition with all packaging. Buyer pays return shipping.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="brand-gradient-bg text-black w-6 h-6 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
              <div>
                <p className="text-white font-medium">Refund Processed</p>
                <p className="text-gray-400 text-sm">Once received and inspected, refund is issued to original payment method within 5-7 business days.</p>
              </div>
            </li>
          </ol>
        </div>
        
        <div className="mt-6 text-center">
          <Link to="/contact" className="btn-primary mr-4">Contact Support</Link>
          <Link to="/faq" className="btn-secondary">View FAQ</Link>
        </div>
      </div>
    </div>
  );
};

export default { AboutPage, ContactPage, FAQPage, ShippingPage };
